import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { MaterialReactTable } from 'material-react-table';
import {
  Box, Button, Alert, TextField, IconButton, Tooltip, MenuItem,
  FormControl, InputLabel, Select, Checkbox, Typography, LinearProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { addCatalogPart, updateCatalogPart, softDeleteCatalogPart } from '../../actions/apiCatalogParts';
import { dealershipsOf } from '../../components/Parts/partsUtils';

const money = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  return Number.isNaN(n) ? '' : n.toFixed(2);
};

const emptyDraft = {
  part_number: '', description: '', price: '',
};

const CatalogPartTable = () => {
  const dispatch = useDispatch();
  const catalog_parts = useSelector(state => state.settings_refresh?.catalog_parts || []);
  const related_installations = useSelector(state => state.settings_dealerships?.related_installations || []);
  const dealership = useSelector(state => state.settings_dealerships?.installation?.subdomain || state.settings_dealerships?.current_dealership || '');

  const dealershipOptions = useMemo(
    () => related_installations.map(inst => ({ value: inst.subdomain, label: inst.softname || inst.subdomain })),
    [related_installations],
  );

  const dealershipLabel = useMemo(() => {
    const map = new Map(dealershipOptions.map(o => [o.value, o.label]));
    return subdomain => map.get(subdomain) || subdomain;
  }, [dealershipOptions]);

  const [draft, setDraft] = useState(emptyDraft);
  const [error, setError] = useState(null);
  const [rowSelection, setRowSelection] = useState({});
  const [massDealerships, setMassDealerships] = useState([]);
  const [updating, setUpdating] = useState(false);

  const numberEdit = (field, { nullable = false } = {}) => ({ row, table }) => ({
    type: 'number',
    defaultValue: row.original[field] ?? '',
    onBlur: async (e) => {
      const raw = e.target.value.trim();
      let value;
      if (raw === '') value = nullable ? null : 0;
      else {
        const n = parseFloat(raw);
        value = Number.isNaN(n) ? (nullable ? null : 0) : n;
      }
      try {
        await dispatch(updateCatalogPart(row.original.id, { [field]: value }));
        table.setEditingCell(null);
      } catch (ex) {
        setError(`Failed to update ${field}`);
      }
    },
  });

  const textEdit = field => ({ row, table }) => ({
    onBlur: async (e) => {
      try {
        await dispatch(updateCatalogPart(row.original.id, { [field]: e.target.value }));
        table.setEditingCell(null);
      } catch (ex) {
        setError(`Failed to update ${field}`);
      }
    },
  });

  const columns = useMemo(() => [
    {
      accessorKey: 'part_number', header: 'Part #', size: 140, enableEditing: true, muiEditTextFieldProps: textEdit('part_number'),
    },
    {
      accessorKey: 'description', header: 'Description', size: 320, enableEditing: true, muiEditTextFieldProps: ({ row, table }) => ({ multiline: true, rows: 2, ...textEdit('description')({ row, table }) }),
    },
    {
      accessorKey: 'price', header: 'Price', size: 100, enableEditing: true, muiEditTextFieldProps: numberEdit('price', { nullable: true }), Cell: ({ cell }) => (cell.getValue() === null || cell.getValue() === undefined ? 'No price' : `$${money(cell.getValue())}`),
    },
    {
      accessorKey: 'active',
      header: 'Active',
      size: 90,
      enableEditing: true,
      muiEditTextFieldProps: ({ row, table }) => ({
        select: true,
        value: String(row.original.active),
        onChange: async (e) => {
          try {
            await dispatch(updateCatalogPart(row.original.id, { active: e.target.value === 'true' }));
            table.setEditingCell(null);
          } catch (ex) {
            setError('Failed to update active');
          }
        },
        children: [
          <MenuItem key="true" value="true">Active</MenuItem>,
          <MenuItem key="false" value="false">Inactive</MenuItem>,
        ],
      }),
      filterVariant: 'select',
      filterSelectOptions: [{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }],
      filterFn: (row, id, filterValue) => (!filterValue ? true : String(row.getValue(id)) === filterValue),
      Cell: ({ cell }) => (cell.getValue() ? 'Yes' : 'No'),
    },
    {
      accessorKey: 'dealership',
      header: 'Dealership',
      size: 200,
      enableEditing: false,
      filterFn: (row, id, filterValue) => {
        if (!filterValue || filterValue.length === 0) return true;
        return dealershipsOf(row.getValue(id)).some(d => filterValue.includes(d));
      },
      Filter: ({ column }) => {
        const selected = column.getFilterValue() || [];
        return (
          <FormControl fullWidth size="small">
            <InputLabel>Dealership</InputLabel>
            <Select
              multiple
              value={selected}
              label="Dealership"
              onChange={(e) => {
                const value = e.target.value;
                if (value.includes('__all__')) {
                  column.setFilterValue(dealershipOptions.map(o => o.value));
                } else if (value.includes('__none__')) {
                  column.setFilterValue(undefined);
                } else {
                  column.setFilterValue(value.length ? value : undefined);
                }
              }}
              renderValue={sel => (sel.length > 2
                ? `${sel.length} selected`
                : sel.map(dealershipLabel).join(', '))}
              MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
            >
              <MenuItem value="__all__">Select All</MenuItem>
              <MenuItem value="__none__">Clear</MenuItem>
              {dealershipOptions.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  <Checkbox checked={selected.includes(opt.value)} />
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
      Cell: ({ cell }) => dealershipsOf(cell.getValue()).map(dealershipLabel).join(', ') || '—',
    },
    {
      id: 'actions',
      header: 'Actions',
      size: 90,
      enableEditing: false,
      Cell: ({ row }) => (
        <Tooltip title="Remove from catalog">
          <IconButton
            size="small"
            color="error"
            onClick={async () => {
              if (!window.confirm(`Remove ${row.original.part_number} from the catalog?`)) return;
              try {
                await dispatch(softDeleteCatalogPart(row.original.id));
              } catch (ex) {
                setError('Failed to remove part');
              }
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [dispatch, dealershipOptions, dealershipLabel]);

  const data = useMemo(() => catalog_parts.map(p => ({
    ...p,
    active: p.active === true || p.active === 'true',
  })), [catalog_parts]);

  const selectedIds = useMemo(
    () => new Set(Object.keys(rowSelection).filter(key => rowSelection[key])),
    [rowSelection],
  );

  const applyDealerships = async (mode) => {
    const selected = data.filter(p => selectedIds.has(String(p.id)));
    if (selected.length === 0 || massDealerships.length === 0) return;

    setUpdating(true);
    setError(null);
    try {
      await Promise.all(selected.map((p) => {
        const value = mode === 'append'
          ? [...new Set([...dealershipsOf(p.dealership), ...massDealerships])].join(',')
          : massDealerships.join(',');
        return dispatch(updateCatalogPart(p.id, { dealership: value }));
      }));
      setRowSelection({});
      setMassDealerships([]);
    } catch (ex) {
      setError('Failed to update dealerships');
    } finally {
      setUpdating(false);
    }
  };

  const setField = (field, value) => setDraft(prev => ({ ...prev, [field]: value }));

  const toNumber = (raw, nullable) => {
    const s = String(raw).trim();
    if (s === '') return nullable ? null : 0;
    const n = parseFloat(s);
    return Number.isNaN(n) ? (nullable ? null : 0) : n;
  };

  const handleAdd = async () => {
    if (!draft.part_number.trim()) {
      setError('Part Number is required');
      return;
    }
    setError(null);
    const payload = {
      part_number: draft.part_number.trim(),
      description: draft.description.trim(),
      price: toNumber(draft.price, true),
      dealership,
    };
    try {
      await dispatch(addCatalogPart(payload));
      setDraft(emptyDraft);
    } catch (ex) {
      setError('Failed to add part');
    }
  };

  return (
    <Box>
      <Box sx={{
 p: 2, mb: 2, bgcolor: '#f8f9fa', borderRadius: 1, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap',
}}
      >
        <TextField size="small" label="Part #" value={draft.part_number} onChange={e => setField('part_number', e.target.value)} sx={{ width: 140 }} />
        <TextField size="small" label="Description" value={draft.description} onChange={e => setField('description', e.target.value)} sx={{ width: 320 }} />
        <TextField size="small" label="Price" type="number" value={draft.price} onChange={e => setField('price', e.target.value)} sx={{ width: 110 }} />
        <Button variant="contained" color="primary" onClick={handleAdd}>Add Part</Button>
      </Box>

      <Box sx={{
        p: 2, mb: 2, bgcolor: '#eef4fa', borderRadius: 1, display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap',
      }}
      >
        <Typography variant="body2">
          <strong>{selectedIds.size}</strong> selected
        </Typography>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Dealerships</InputLabel>
          <Select
            multiple
            value={massDealerships}
            label="Dealerships"
            onChange={e => setMassDealerships(e.target.value)}
            renderValue={sel => sel.map(dealershipLabel).join(', ')}
            MenuProps={{ PaperProps: { style: { maxHeight: 320 } } }}
          >
            {dealershipOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                <Checkbox checked={massDealerships.includes(opt.value)} />
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          disabled={selectedIds.size === 0 || massDealerships.length === 0 || updating}
          onClick={() => applyDealerships('override')}
        >
          Override
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={selectedIds.size === 0 || massDealerships.length === 0 || updating}
          onClick={() => applyDealerships('append')}
        >
          Append
        </Button>
        {updating && <LinearProgress sx={{ flex: 1, ml: 2 }} />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <MaterialReactTable
        columns={columns}
        data={data}
        enableEditing
        editDisplayMode="cell"
        enableRowSelection
        enableMultiRowSelection
        getRowId={row => String(row.id)}
        onRowSelectionChange={setRowSelection}
        state={{ rowSelection }}
        initialState={{ density: 'compact', showColumnFilters: true }}
      />
    </Box>
  );
};

export default CatalogPartTable;
